/* eslint-disable @compiled/shorthand-property-sorting */
/** @jsx jsx */
import React, { useCallback } from 'react';
import { css, jsx } from '@compiled/react';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { IconButton } from '@atlaskit/button/new';
import CheckIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import CopyIcon from '@atlaskit/icon/core/copy';
import { xcss, Flex, Box } from '@atlaskit/primitives';
import {
	Card,
	TitleBlock,
	SmartLinkSize,
	SmartLinkTheme,
	SmartLinkPosition,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { IssueLinkType } from '@atlassian/jira-issue-shared-types/src/common/types/linked-issue-type.tsx';
import {
	useRelatedIssuesActions,
	useRelatedIssuesState,
	type RelatedIssue,
	type InvokedFrom,
} from '../../controllers/context.tsx';
import { getRelatesToIssueLinkType, useRelatesToIssueLinkType } from '../../utils/index.tsx';
import type { onIssueLinkSave } from '../types.tsx';
import messages from './messages.tsx';

const ACTION_BUTTON_COMPONENT_SELECTOR =
	'ai-improve-issue-ai-related-issues-draft-list-row-action-button';

type Props = {
	onIssueLinkSave: onIssueLinkSave;
	invokedFrom?: InvokedFrom;
	onCopyButtonClick?: (issueKey: string) => void;
	onSimilarIssueLinkGICButtonClick?: ((relatedIssue: RelatedIssue) => void) | null;
	issueLinkTypes: IssueLinkType[];
};

export const RelatedIssuesDraftList = ({
	invokedFrom,
	onIssueLinkSave,
	onCopyButtonClick,
	onSimilarIssueLinkGICButtonClick,
	issueLinkTypes,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const { resourceSuggestions } = useRelatedIssuesState();
	const { acceptSuggestion, acceptAllSuggestions, discardSuggestion, discardAllSuggestions } =
		useRelatedIssuesActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	let relatedToIssueType: IssueLinkType;
	if (fg('remove-issue-link-types-dfo')) {
		relatedToIssueType = getRelatesToIssueLinkType(issueLinkTypes);
	} else {
		// eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
		relatedToIssueType = useRelatesToIssueLinkType();
	}

	const isSimilarWorkItemsUIFixesEnabled =
		invokedFrom === 'globalIssueCreate' && fg('similar_items_in_gic_ui_fixes');

	const draftSuggestions = resourceSuggestions?.filter(
		(suggestion) =>
			suggestion.resourceStatus === 'DRAFT' || suggestion.resourceStatus === 'SELECTED',
	);

	const onSingleSuggestionAccept = useCallback(
		(linkSuggestionsRequest: RelatedIssue) => {
			const optimisticId = `OPTIMISTIC_LINKED_ISSUE_${Date.now()}_${linkSuggestionsRequest.id}`;

			onIssueLinkSave(
				optimisticId,
				linkSuggestionsRequest,
				relatedToIssueType,
				createAnalyticsEvent({}),
			);
		},
		[createAnalyticsEvent, onIssueLinkSave, relatedToIssueType],
	);

	const onAcceptSuggestion = useCallback(
		(linkSuggestionsRequest: RelatedIssue) => {
			/**
			 * We have this condition here to handle the case
			 * where there is only one suggestion and the user clicks on the accept button.
			 */
			if (draftSuggestions && draftSuggestions.length === 1) {
				acceptAllSuggestions();
			} else {
				acceptSuggestion(linkSuggestionsRequest);
			}
			onSingleSuggestionAccept(linkSuggestionsRequest);

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult actioned',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiResultAction: 'inlineIssueAccepted',
					...(fg('thor-additional-analytics-suggested-related-issue') && {
						linkedIssueId: linkSuggestionsRequest.id,
					}),
				},
			);
		},
		[
			acceptAllSuggestions,
			acceptSuggestion,
			createAnalyticsEvent,
			draftSuggestions,
			onSingleSuggestionAccept,
		],
	);

	const onAcceptSuggestionInGIC = useCallback(
		(linkSuggestionsRequest: RelatedIssue) => {
			acceptSuggestion(linkSuggestionsRequest);
			onSimilarIssueLinkGICButtonClick?.(linkSuggestionsRequest);
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult actioned',
				'aiRelatedIssuesSuggestionResultBoxGIC',
				{
					aiResultAction: 'inlineIssueAcceptedGIC',
					...(fg('thor-additional-analytics-suggested-related-issue') && {
						linkedIssueId: linkSuggestionsRequest.id,
					}),
				},
			);
		},
		[acceptSuggestion, createAnalyticsEvent, onSimilarIssueLinkGICButtonClick],
	);

	const onDiscardSuggestion = useCallback(
		(suggestionToBeDiscarded: RelatedIssue) => {
			/**
			 * We have this condition here to handle the case
			 * where there is only one suggestion and the user clicks on the discard button.
			 */
			if (draftSuggestions && draftSuggestions.length === 1) {
				discardAllSuggestions();
			} else {
				discardSuggestion(suggestionToBeDiscarded);
			}

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiResult actioned',
				'aiRelatedIssuesSuggestionResultBox',
				{
					aiResultAction: 'inlineIssueDeclined',
					...(fg('thor-additional-analytics-suggested-related-issue') && {
						linkedIssueId: suggestionToBeDiscarded.id,
					}),
				},
			);
		},
		[createAnalyticsEvent, discardAllSuggestions, discardSuggestion, draftSuggestions],
	);

	const onContainerRef = useCallback((element: HTMLDivElement | null) => {
		if (element && typeof element.scrollIntoView === 'function') {
			element.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, []);

	const cards = draftSuggestions?.map(({ resource }) => {
		const handleOpenLinkInNewTab = (resourceUrl: string) => {
			if (resourceUrl) {
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.open(resourceUrl, '_blank');
				fireTrackAnalytics(
					createAnalyticsEvent({}),
					'aiResult actioned',
					'aiRelatedIssuesSuggestionOpenedLinkInNewTab',
					{
						aiResultAction: 'openedLinkInNewTab',
					},
				);
			}
		};
		return (
			<div
				css={[baseRowStyles, isSimilarWorkItemsUIFixesEnabled ? rowBorderStyles : rowStyles]}
				key={resource.id}
			>
				<Flex alignItems="center">
					<Card
						appearance="block"
						url={resource.issueLink}
						showHoverPreview
						ui={{
							size: SmartLinkSize.Medium,
							hideElevation: true,
							hideBackground: true,
							theme: SmartLinkTheme.Black,
							hidePadding: true,
						}}
					>
						<TitleBlock
							hideTitleTooltip
							position={SmartLinkPosition.Center}
							testId={`ai-related-issues.ui.related-issues-draft-list.title-block-${resource.id}`}
						/>
					</Card>
				</Flex>
				<Flex xcss={actionStyles}>
					{invokedFrom === 'globalIssueCreate' &&
					expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false) ? null : (
						<IconButton
							testId={`ai-related-issues.ui.related-issues-draft-list.accept-suggestion-icon-button-${resource.id}`}
							data-component-selector={`${ACTION_BUTTON_COMPONENT_SELECTOR}`}
							icon={CheckIcon}
							label={formatMessage(messages.link)}
							spacing="default"
							appearance="subtle"
							onClick={() => onAcceptSuggestion(resource)}
							isTooltipDisabled={false}
							tooltip={{
								testId:
									'ai-related-issues.ui.issue-breakdown-draft-list.suggestion-list-item.icon-button.tooltip',
								position: 'bottom',
							}}
						/>
					)}

					{invokedFrom === 'globalIssueCreate' &&
					onCopyButtonClick &&
					expVal('similar_work_items_copy_field_milestone-2', 'isEnabled', false) ? (
						<IconButton
							testId={`ai-related-issues.ui.related-issues-draft-list.accept-suggestion-icon-button-${resource.id}`}
							data-component-selector={`${ACTION_BUTTON_COMPONENT_SELECTOR}`}
							icon={CopyIcon}
							label={formatMessage(messages.link)}
							spacing="default"
							appearance="subtle"
							onClick={() => onCopyButtonClick(resource.issueKey)}
							isTooltipDisabled={false}
							tooltip={{
								testId:
									'ai-related-issues.ui.issue-breakdown-draft-list.suggestion-list-item.icon-button.tooltip',
								position: 'bottom',
							}}
						/>
					) : null}

					{invokedFrom === 'globalIssueCreate' &&
					onSimilarIssueLinkGICButtonClick &&
					fg('similar_work_items_in_gic_link_work_items') ? (
						<IconButton
							testId={`ai-related-issues.ui.related-issues-draft-list.accept-suggestion-icon-button-${resource.id}`}
							data-component-selector={`${ACTION_BUTTON_COMPONENT_SELECTOR}`}
							icon={CheckIcon}
							label={formatMessage(messages.link)}
							spacing="default"
							appearance="subtle"
							onClick={() => onAcceptSuggestionInGIC(resource)}
							isTooltipDisabled={false}
							tooltip={{
								testId:
									'ai-related-issues.ui.issue-breakdown-draft-list.suggestion-list-item.icon-button.tooltip',
								position: 'bottom',
							}}
						/>
					) : null}

					{invokedFrom === 'globalIssueCreate' &&
					expVal('proactive_similar_work_items_in_gic_experiment', 'isEnabled', false) ? (
						<IconButton
							testId={`ai-related-issues.ui.related-issues-draft-list.discard-suggestion-icon-button-${resource.id}`}
							data-component-selector={`${ACTION_BUTTON_COMPONENT_SELECTOR}`}
							icon={LinkExternalIcon}
							label={formatMessage(messages.openInNewTab)}
							spacing="default"
							appearance="subtle"
							onClick={() => handleOpenLinkInNewTab(resource.issueLink)}
							isTooltipDisabled={false}
							tooltip={{
								testId:
									'ai-related-issues.ui.issue-breakdown-draft-list.suggestion-list-item.icon-button.tooltip',
								position: 'bottom',
							}}
						/>
					) : (
						<IconButton
							testId={`ai-related-issues.ui.related-issues-draft-list.discard-suggestion-icon-button-${resource.id}`}
							data-component-selector={`${ACTION_BUTTON_COMPONENT_SELECTOR}`}
							icon={CrossIcon}
							label={formatMessage(messages.remove)}
							spacing="default"
							appearance="subtle"
							onClick={() => onDiscardSuggestion(resource)}
							isTooltipDisabled={false}
							tooltip={{
								testId:
									'ai-related-issues.ui.issue-breakdown-draft-list.suggestion-list-item.icon-button.tooltip',
								position: 'bottom',
							}}
						/>
					)}
				</Flex>
			</div>
		);
	});

	return <Box ref={onContainerRef}>{cards}</Box>;
};

const baseRowStyles = css({
	display: 'flex',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button']": {
		opacity: '0',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button-is-linking']":
		{ opacity: '1' },

	'&:hover, &:focus-within': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		textDecoration: 'none',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"[data-component-selector='ai-improve-issue-ai-related-issues-draft-list-row-action-button']": {
			opacity: '1',
		},
	},
});

const rowBorderStyles = css({
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	borderTop: `1px solid ${token('color.border')}`,
});

const rowStyles = css({
	paddingInline: token('space.200'),
	paddingBlock: token('space.150'),
	borderWidth: token('border.width'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	backgroundColor: token('elevation.surface'),
});

const actionStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	columnGap: 'space.150',
});
